import React from 'react';
import { Agent, LLM_PROVIDERS } from '../types/agent';
import { Edit2, Trash2, CheckCircle, XCircle, Zap, DollarSign } from 'lucide-react';

interface AgentsListProps {
  agents: Agent[];
  onSelect: (agent: Agent) => void;
  onDelete: (id: number) => void;
  loading: boolean;
}

const AgentsList: React.FC<AgentsListProps> = ({ agents, onSelect, onDelete, loading }) => {
  if (loading) {
    return <div className="loading">Cargando agentes...</div>;
  }

  if (agents.length === 0) {
    return (
      <div className="empty-state">
        <p>No hay agentes registrados</p>
        <p className="hint">Crea tu primer agente usando el botón "Crear"</p>
      </div>
    );
  }

  const getProviderInfo = (providerId: string) => {
    return LLM_PROVIDERS.find(p => p.id === providerId);
  };

  const getModelInfo = (providerId: string, modelId: string) => {
    const provider = getProviderInfo(providerId);
    return provider?.models.find(m => m.id === modelId);
  };

  return (
    <div className="agents-list">
      <table className="data-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Descripción</th>
            <th>Proveedor LLM</th>
            <th>Modelo</th>
            <th>Prompts</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {agents.map((agent) => {
            const providerInfo = getProviderInfo(agent.llmProvider || 'qwen');
            const modelInfo = getModelInfo(agent.llmProvider || 'qwen', agent.llmModel || 'qwen-turbo');
            
            return (
              <tr key={agent.id}>
                <td className="agent-name">{agent.name}</td>
                <td className="agent-description">{agent.description}</td>
                <td>
                  <div className="provider-info">
                    <span className={`provider-badge ${agent.llmProvider}`}>
                      <Zap size={12} />
                      {providerInfo?.name || agent.llmProvider}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="model-info">
                    <div className="model-name">{modelInfo?.name || agent.llmModel}</div>
                    {modelInfo?.pricing && (
                      <div className="model-pricing">
                        <DollarSign size={10} />
                        ${modelInfo.pricing.input}/${modelInfo.pricing.output}
                      </div>
                    )}
                  </div>
                </td>
                <td className="prompts-info">
                  <div>Sistema: {agent.systemPromptId}</div>
                  <div>Conv: {agent.conversationPromptId}</div>
                </td>
                <td>
                  {agent.isActive ? (
                    <span className="status active">
                      <CheckCircle size={16} /> Activo
                    </span>
                  ) : (
                    <span className="status inactive">
                      <XCircle size={16} /> Inactivo
                    </span>
                  )}
                </td>
                <td>
                  <div className="actions">
                    <button
                      className="action-btn edit"
                      onClick={() => onSelect(agent)}
                      title="Editar"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => onDelete(agent.id)}
                      title="Desactivar"
                      disabled={!agent.isActive}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <style>{`
        .agents-list {
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          min-width: 900px;
        }

        .data-table th {
          text-align: left;
          padding: 12px;
          background: #f5f5f5;
          border-bottom: 2px solid #e0e0e0;
          font-weight: 600;
          color: #555;
          white-space: nowrap;
        }

        .data-table td {
          padding: 12px;
          border-bottom: 1px solid #e0e0e0;
          vertical-align: top;
        }

        .data-table tr:hover {
          background: #f8f8f8;
        }

        .agent-name {
          font-weight: 500;
          color: #333;
          min-width: 120px;
        }

        .agent-description {
          color: #666;
          font-size: 14px;
          max-width: 200px;
          word-wrap: break-word;
        }

        .provider-info {
          display: flex;
          align-items: center;
        }

        .provider-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .provider-badge.qwen {
          background: #e3f2fd;
          color: #1565c0;
        }

        .provider-badge.openai {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .provider-badge.anthropic {
          background: #fff3e0;
          color: #ef6c00;
        }

        .provider-badge.gemini {
          background: #fce4ec;
          color: #c2185b;
        }

        .model-info {
          min-width: 120px;
        }

        .model-name {
          font-weight: 500;
          color: #333;
          font-size: 13px;
        }

        .model-pricing {
          display: flex;
          align-items: center;
          gap: 2px;
          color: #666;
          font-size: 11px;
          margin-top: 2px;
        }

        .prompts-info {
          font-size: 12px;
          color: #666;
          font-family: monospace;
          line-height: 1.4;
        }

        .status {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
        }

        .status.active {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .status.inactive {
          background: #ffebee;
          color: #c62828;
        }

        .actions {
          display: flex;
          gap: 8px;
        }

        .action-btn {
          padding: 6px;
          border: 1px solid #ddd;
          background: white;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .action-btn:hover {
          background: #f5f5f5;
        }

        .action-btn.edit {
          color: #1976d2;
        }

        .action-btn.edit:hover {
          border-color: #1976d2;
        }

        .action-btn.delete {
          color: #d32f2f;
        }

        .action-btn.delete:hover:not(:disabled) {
          border-color: #d32f2f;
        }

        .action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #666;
        }

        .empty-state p {
          margin: 10px 0;
        }

        .hint {
          font-size: 14px;
          color: #999;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        @media (max-width: 1200px) {
          .agent-description {
            max-width: 150px;
          }
        }

        @media (max-width: 768px) {
          .data-table {
            font-size: 12px;
          }
          
          .data-table th,
          .data-table td {
            padding: 8px;
          }
          
          .agent-description {
            max-width: 100px;
          }
        }
      `}</style>
    </div>
  );
};

export default AgentsList;